import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import {
  Menu, X, ChevronDown, HelpCircle, BookOpen, Sparkles,
  Mic, FileText, FolderKanban, Banknote, ShieldCheck,
  LayoutGrid, ArrowRight, MapPin,
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/* ── Slim menu: 6 flagship features, each links straight to its showcase page ── */
const featureLinks = [
  { label: "AI Phone Receptionist", desc: "Answers missed calls, books jobs", path: "/features/ai-receptionist", icon: Mic, badge: "NEW" },
  { label: "Voice Quoting", desc: "Quote from the van, by voice", path: "/features/owner-mode", icon: Mic, badge: "NEW" },
  { label: "Quotes & Invoices", desc: "Branded PDFs, one-click convert", path: "/features/quoting", icon: FileText },
  { label: "Project Manager", desc: "Timelines, stages & progress", path: "/features/project-manager", icon: FolderKanban },
  { label: "Card Payments & Get Paid", desc: "Customers pay by card, chase less", path: "/features#invoicing", icon: Banknote },
  { label: "Trade Certificates", desc: "EIC, EICR, CP12, PAT & more", path: "/trade-certificates", icon: ShieldCheck },
];

const Pill = ({ badge }) => {
  if (badge === "NEW") return <span className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-white leading-none">NEW</span>;
  if (badge === "SOON") return <span className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-300 leading-none">SOON</span>;
  return null;
};

export const PublicNav = ({ onHowItWorks, onComingSoonNotify }) => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [blogPublic, setBlogPublic] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/blog/status`).then(r => setBlogPublic(r.data.blog_public)).catch(() => {});
  }, []);

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const featuresActive = location.pathname.startsWith("/features") || location.pathname.startsWith("/trade-certificates");

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200/50" data-testid="public-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img
                src={`${API_URL}/api/uploads/quotesorted_logo.png`}
                alt="QuoteSorted"
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {/* Features dropdown — slim, 6 flagship links */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-sm gap-1 ${featuresActive ? "text-orange-600 font-semibold bg-orange-50" : "text-slate-600 hover:text-slate-900"}`}
                  onClick={() => setFeaturesOpen(!featuresOpen)}
                  data-testid="nav-features"
                >
                  Features
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${featuresOpen ? "rotate-180" : ""}`} />
                </Button>

                {featuresOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setFeaturesOpen(false)} />
                    <div className="absolute top-full left-0 mt-2 w-[360px] bg-white rounded-2xl shadow-2xl border border-slate-200 z-50 overflow-hidden p-2" data-testid="features-menu">
                      <div className="space-y-0.5">
                        {featureLinks.map((it) => (
                          <Link
                            key={it.path}
                            to={it.path}
                            onClick={() => setFeaturesOpen(false)}
                            className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
                            data-testid={`features-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                          >
                            <div className="w-8 h-8 rounded-lg bg-orange-50 group-hover:bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors">
                              <it.icon className="w-4 h-4 text-orange-600" strokeWidth={2} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-sm text-slate-900 group-hover:text-orange-700 transition-colors flex items-center gap-1.5">
                                {it.label}
                                <Pill badge={it.badge} />
                              </p>
                              <p className="text-xs text-slate-500 leading-snug">{it.desc}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                      <Link
                        to="/features"
                        onClick={() => setFeaturesOpen(false)}
                        className="mt-1 flex items-center justify-center gap-1.5 text-sm font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-xl py-2.5 transition-colors"
                        data-testid="features-see-all"
                      >
                        <LayoutGrid className="w-4 h-4" />
                        See all features
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </>
                )}
              </div>

              {/* How it works */}
              {onHowItWorks && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-sm text-slate-600 hover:text-slate-900 gap-1"
                  onClick={onHowItWorks}
                  data-testid="nav-how-it-works"
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                  How it works
                </Button>
              )}

              {/* Pricing */}
              <Link to="/pricing">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-sm ${isActive("/pricing") ? "text-orange-600 font-semibold bg-orange-50" : "text-slate-600 hover:text-slate-900"}`}
                  data-testid="nav-pricing"
                >
                  Pricing
                </Button>
              </Link>

              {/* Directory */}
              <Link to="/find">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-sm ${isActive("/find") ? "text-orange-600 font-semibold bg-orange-50" : "text-slate-600 hover:text-slate-900"}`}
                  data-testid="nav-directory"
                >
                  Directory
                </Button>
              </Link>

              {/* Blog */}
              {blogPublic && (
                <Link to="/blog">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`text-sm gap-1 ${isActive("/blog") ? "text-orange-600 font-semibold bg-orange-50" : "text-slate-600 hover:text-slate-900"}`}
                    data-testid="nav-blog"
                  >
                    <BookOpen className="w-3.5 h-3.5" />
                    Blog
                  </Button>
                </Link>
              )}

              {/* Roadmap */}
              <Link to="/roadmap">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-sm ${isActive("/roadmap") ? "text-orange-600 font-semibold bg-orange-50" : "text-slate-600 hover:text-slate-900"}`}
                  data-testid="nav-roadmap"
                >
                  Roadmap
                </Button>
              </Link>

              <div className="w-px h-6 bg-slate-200 mx-1" />

              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-slate-600 text-sm" data-testid="nav-signin">
                  Sign in
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white text-sm" data-testid="nav-get-started">
                  Get Started
                </Button>
              </Link>
            </div>

            {/* Mobile: Sign in + Hamburger */}
            <div className="flex lg:hidden items-center gap-2">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-sm text-slate-600">Sign in</Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileOpen(true)}
                data-testid="mobile-menu-btn"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <div className="fixed top-0 right-0 bottom-0 w-72 bg-white z-50 shadow-2xl lg:hidden overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <span className="font-bold text-slate-900">Menu</span>
              <Button variant="ghost" size="sm" onClick={() => setMobileOpen(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 space-y-1">
              {/* Flagship features */}
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider px-3 pt-1 pb-1">Features</p>
              {featureLinks.map((it) => (
                <Link
                  key={it.path}
                  to={it.path}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                  data-testid={`mobile-features-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                >
                  <span className="flex items-center gap-2.5">
                    <it.icon className="w-4 h-4 text-orange-600" />
                    {it.label}
                  </span>
                  <Pill badge={it.badge} />
                </Link>
              ))}
              <Link
                to="/features"
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between px-3 py-3 rounded-xl text-sm font-bold mt-1 bg-gradient-to-r from-orange-50 to-amber-50 text-orange-700 border border-orange-200 hover:from-orange-100 hover:to-amber-100 transition-colors"
                data-testid="mobile-all-features"
              >
                <span className="flex items-center gap-2.5">
                  <LayoutGrid className="w-4 h-4" />
                  See all features
                </span>
                <ArrowRight className="w-4 h-4" />
              </Link>

              <div className="pt-3 mt-2 border-t border-slate-100 space-y-1">
                <Link
                  to="/pricing"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive("/pricing") ? "text-orange-600 bg-orange-50" : "text-slate-700 hover:bg-slate-50"}`}
                >
                  Pricing
                </Link>
                <Link
                  to="/find"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive("/find") ? "text-orange-600 bg-orange-50" : "text-slate-700 hover:bg-slate-50"}`}
                >
                  <MapPin className="w-4 h-4" />
                  Directory
                </Link>
                {onHowItWorks && (
                  <button
                    onClick={() => { onHowItWorks(); setMobileOpen(false); }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors w-full text-left"
                    data-testid="mobile-how-it-works"
                  >
                    <HelpCircle className="w-4 h-4" />
                    How it works
                  </button>
                )}
                {blogPublic && (
                  <Link
                    to="/blog"
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive("/blog") ? "text-orange-600 bg-orange-50" : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <BookOpen className="w-4 h-4" />
                    Blog
                  </Link>
                )}
                <Link
                  to="/roadmap"
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive("/roadmap") ? "text-orange-600 bg-orange-50" : "text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid="mobile-nav-roadmap"
                >
                  <Sparkles className="w-4 h-4" />
                  Roadmap
                </Link>
              </div>

              <div className="pt-4 mt-4 border-t space-y-2">
                <Link to="/login" onClick={() => setMobileOpen(false)}>
                  <Button variant="outline" className="w-full" size="sm">Sign in</Button>
                </Link>
                <Link to="/register" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full bg-orange-600 hover:bg-orange-700 text-white" size="sm">
                    Get Started Free
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
